import React from 'react';
import { Box, Typography, Button, Breadcrumbs, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { NavigateNext as NavigateNextIcon } from '@mui/icons-material';

const PRIMARY = '#29204C';
const SUCCESS = '#68B977';

/**
 * PageHeader Component - Consistent page header with breadcrumbs
 *
 * @param {String} title - Page title
 * @param {String} subtitle - Optional subtitle
 * @param {Object} icon - Icon component to display
 * @param {Object} action - Action button configuration { label, icon, onClick, variant, color }
 * @param {Array} breadcrumbs - Array of breadcrumb items { label, link }
 */
const PageHeader = ({
  title,
  subtitle,
  icon: Icon,
  action,
  breadcrumbs = [],
}) => {
  return (
    <Box sx={{ mb: 4 }}>
      {/* Breadcrumbs */}
      {breadcrumbs.length > 0 && (
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ mb: 2 }}
        >
          {breadcrumbs.map((crumb, index) => {
            const isLast = index === breadcrumbs.length - 1;
            return isLast ? (
              <Typography key={index} color="text.primary" fontSize="0.875rem">
                {crumb.label}
              </Typography>
            ) : (
              <Link
                key={index}
                component={RouterLink}
                to={crumb.link || '#'}
                underline="hover"
                color="inherit"
                fontSize="0.875rem"
              >
                {crumb.label}
              </Link>
            );
          })}
        </Breadcrumbs>
      )}

      {/* Title and Action */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: subtitle ? 1 : 0 }}>
            {Icon && (
              <Box
                sx={{
                  bgcolor: `${SUCCESS}15`,
                  borderRadius: 2,
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Icon sx={{ fontSize: 32, color: SUCCESS }} />
              </Box>
            )}
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: PRIMARY,
                fontSize: { xs: '1.75rem', sm: '2rem' },
              }}
            >
              {title}
            </Typography>
          </Box>
          {subtitle && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ ml: Icon ? 8 : 0 }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>

        {action && (
          <Button
            variant={action.variant || 'contained'}
            color={action.color || 'success'}
            onClick={action.onClick}
            startIcon={action.icon}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1.5,
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: action.variant === 'contained' ? 3 : 0,
              '&:hover': {
                boxShadow: action.variant === 'contained' ? 6 : 0,
              },
            }}
          >
            {action.label}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default PageHeader;
